import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ClearLocalStorage, Toast } from '@stylehaven/components';

import { CurrentUser, LoadingBehaviorSubject, selectAll, ToastService, UnsetToasts } from '@digihaven/shared';

import { Logout } from '@actions/authentication.actions';
import { UnsetFunctionalities } from '@actions/functionality.action';
import { UnsetTokens } from '@actions/token.actions';
import { UnsetUser } from '@actions/user.actions';
import { AuthorizationService } from '@common/services/authorization/authorization.service';

import { ApplicationState } from 'state';

@Injectable()
export class BootstrapSandboxService {
    toasts$: Observable<Toast[]>;
    isAuthenticated$: Observable<boolean>;
    isTokenValid$: Observable<boolean>;
    isTokenLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();

    constructor(
        private authorizationService: AuthorizationService,
        private store: Store<ApplicationState>,
        private toastService: ToastService
    ) {
        this.toasts$ = this.buildToastStream();
        this.isAuthenticated$ = this.buildIsAuthenticatedStream();
        this.isTokenValid$ = this.buildIsTokenValidStream();
    }

    deleteToast(toastId: number): void {
        this.toastService.delete(toastId);
    }

    clearStore(): void {
        this.store.dispatch(new Logout());
        this.store.dispatch(new UnsetTokens());
        this.store.dispatch(new UnsetUser());
        this.store.dispatch(new UnsetFunctionalities());
        this.store.dispatch(UnsetToasts());

        ClearLocalStorage();
    }

    private buildToastStream(): Observable<Toast[]> {
        return this.store.select(store => selectAll(store.sharedToast));
    }

    private buildIsAuthenticatedStream(): Observable<boolean> {
        return this.store.select(state => state.authentication.isAuthenticated);
    }

    private buildIsTokenValidStream(): Observable<boolean> {
        return this.isAuthenticated$.pipe(
            tap(() => this.isTokenLoading$.startLoading()),
            switchMap(this.fetchUserIfAuthenticated.bind(this)),
            tap(() => this.isTokenLoading$.stopLoading())
        );
    }

    private fetchUserIfAuthenticated(isAuthenticated: boolean): Observable<boolean> {
        if (isAuthenticated) {
            return this.fetchUserInfo().pipe(
                map(currentUser => !!currentUser),
                catchError(() => of(false))
            );
        } else {
            return of(false);
        }
    }

    private fetchUserInfo(): Observable<CurrentUser> {
        return this.authorizationService.getCurrentUser();
    }
}
